import { format, fromUnixTime } from 'date-fns'

import type {
  ActionDrawerState,
  DataSeries,
  DrilldownPayload,
  GlobalCopy,
  Transaction,
} from '../../../core'
import { DATE_FORMATS_INTL } from '../../../core/constants'
import type { DataSet } from '../../../core/types/InsightsInstanceSlotData'
import type { TransactionFilter } from '../../../core/stores/TransactionStore'
import {
  buildChartData,
  buildDataSeries,
  type ChartDataSeriesItem,
} from '../../blocks/ChartBlocks/BarchartBlock/utils'
import {
  layout,
  FALLBACK,
  DRILLDOWN_VARIANTS,
  DrillDownVariant,
  DrillDownVariantConfig,
} from './layout'
import { TransactionHistoryBlock } from '../../../core/types/localization/InsightsFeedCopy'

interface DefaultSelectionProp {
  chartTabCount: number
  comparisonTabCount: number
  actionDrawer: ActionDrawerState
  beatGuid: string
}
export const getDrillDownConfig = (variant?: string): DrillDownVariantConfig =>
  layout[variant as DrillDownVariant] ?? FALLBACK

// The shaped chart inputs a drill-down needs, derived once from the beat's data_series. `dataSeries`
// is the flat per-bar source (indexes the filterStrategy); `chartData` feeds the single-bar chart;
// `comparisonBars` is reserved for the double-bar MR (empty until then).
export interface FlattenedDataSeries {
  dataSeries: ChartDataSeriesItem[]
  chartData: ReturnType<typeof buildChartData>
  comparisonBars: DataSet[]
}

/**
 * Shapes the beat's raw data_series into the chart inputs for the variant's top kind. This MR handles
 * the single-bar family (singleBar / upcomingBill); the double-bar and tabsOnly branches are stubbed
 * for the next MR so the call site is already wired.
 */
interface FlattenedDataSeriesInput {
  data: DataSeries | undefined
  ariaLabel: string
  top: DrillDownVariantConfig['top']
  // UpcomingBill has no data_series; it builds its bars from these instead.
  beatTransactions: Transaction[]
  payload: { date?: string; amount?: number } | undefined
}

export const getFlattenedDataSeries = ({
  data,
  ariaLabel,
  top,
  beatTransactions,
  payload,
}: FlattenedDataSeriesInput): FlattenedDataSeries => {
  // UpcomingBill has no backend data_series: synthesize the bars from the beat's recurring-bill
  // transactions + the projected bill, and label each bar by day (MONTH_DAY) since a bar is one bill.
  if (top === 'upcomingBill') {
    const dataSeries = buildUpcomingBillSeries(beatTransactions, payload)
    return {
      dataSeries,
      chartData: buildChartData(dataSeries, ariaLabel, DATE_FORMATS_INTL.MONTH_DAY),
      comparisonBars: [],
    }
  }

  // Single-bar family (THIS MR): the backend sends data_series newest-first, so normalize it
  // date-ascending here. chartData/tabs and the filter's dataSeries[selectedIndex] indexing both read
  // this one array, so they stay aligned and the last tab is the most recent month.
  const dataSeries = [...buildDataSeries(data)].sort((a, b) => {
    if (!a.date || !b.date) return 0

    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })

  // NEXT MR — double bar: comparison beats build paired bars instead of chartData.
  //   if (top === "doubleBar") {
  //     return { dataSeries, chartData: [], comparisonBars: buildComparisonSeries(data, ariaLabel) };
  //   }
  //
  // NEXT MR — tabsOnly: donut-drawer beats render no top chart.
  //   if (top === "tabsOnly") {
  //     return { dataSeries, chartData: [], comparisonBars: [] };
  //   }

  return { dataSeries, chartData: buildChartData(dataSeries, ariaLabel), comparisonBars: [] }
}

// Sentinel guid for the synthesized projected bill. It intentionally matches no real store
// transaction, so the projected tab's guid filter yields an empty list (projected-bar-only scope).
export const PROJECTED_BILL_GUID = 'TRN-projected-bill'

// One bar in the UpcomingBill chart. `date` is 'yyyy-MM-dd' so buildChartData/parseISO can read both
// historical unix timestamps and the ISO payload date after normalization here.
export interface UpcomingBillBar {
  date: string
  amount: number
  guid: string
  category_guid?: string
}

const toUpcomingBillBar = (transaction: Transaction): UpcomingBillBar => ({
  date: format(fromUnixTime(transaction.date), 'yyyy-MM-dd'),
  amount: transaction.amount,
  guid: transaction.guid,
  category_guid: transaction.top_level_category_guid ?? transaction.category_guid,
})

/**
 * UpcomingBill has no data_series: build the bar series straight from the beat's recurring-bill
 * transactions plus the synthesized projected bill from payload (mirrors pulse's magicTransactions).
 * Historical bills are de-duped by date and each keeps its real transaction guid so the guid filter
 * finds it in the store; the projected bar (cloned category from the latest bill) carries the
 * PROJECTED_BILL_GUID sentinel. The result is sorted date-ascending so the projected bill lands last.
 */
export const buildUpcomingBillSeries = (
  transactions: Transaction[] = [],
  payload: { date?: string; amount?: number } | undefined,
): UpcomingBillBar[] => {
  if (!transactions.length || payload?.date == null || payload?.amount == null) {
    return []
  }

  const sorted = [...transactions].sort((a, b) => a.date - b.date)

  const seenDates = new Set<number>()
  const history: UpcomingBillBar[] = []
  for (const transaction of sorted) {
    if (seenDates.has(transaction.date)) continue
    seenDates.add(transaction.date)
    history.push(toUpcomingBillBar(transaction))
  }

  const latestBill = sorted[sorted.length - 1]
  const projected: UpcomingBillBar = {
    date: payload.date,
    amount: payload.amount,
    guid: PROJECTED_BILL_GUID,
    category_guid: latestBill.top_level_category_guid ?? latestBill.category_guid,
  }

  return [...history, projected]
}

export const getDrawerHeaderTitle = (
  variant: string | null | undefined,
  copy: GlobalCopy,
): string => {
  const transactionHistoryTitle = copy.insights_feed?.general?.transaction_history_title ?? ''
  const drawerTitleCopy = copy.insights_feed?.transaction_history_block as TransactionHistoryBlock

  if (variant === DRILLDOWN_VARIANTS.TRANSACTION) return transactionHistoryTitle

  // Safely access the drawer title from transaction_history_block
  const key = `${variant}_drawer_title` as keyof TransactionHistoryBlock
  return (drawerTitleCopy?.[key] as string) ?? transactionHistoryTitle
}

export const toStoreFilter = (
  filter: TransactionFilter,
  defaults: { allAccountGuids: string[]; displayedDateRange: { start: Date; end: Date } },
): TransactionFilter => ({
  accounts: filter.accounts ?? defaults.allAccountGuids,
  dateRange: filter.dateRange ?? defaults.displayedDateRange,
  custom: filter.custom ?? (() => true),
  showSplits: filter.showSplits ?? false,
})

export const getDefaultSelection = ({
  chartTabCount,
  actionDrawer,
  beatGuid,
}: DefaultSelectionProp): { chartBarIndex: number; subTabIndex: number } => {
  const openedViaChartBar = actionDrawer.guid === beatGuid && actionDrawer.trigger === 'chartBar'

  // actionDrawer.context is an opaque `unknown` bag; narrow it to the shape the chart blocks write.
  const context = actionDrawer.context as DrilldownPayload | undefined
  const subTabIndex = openedViaChartBar ? (context?.tabIndex ?? 0) : 0

  if (openedViaChartBar) {
    const clamped = Math.min(
      Math.max(context?.chartBarIndex ?? 0, 0),
      Math.max(0, chartTabCount - 1),
    )
    return { chartBarIndex: clamped, subTabIndex }
  }

  return { chartBarIndex: Math.max(0, chartTabCount - 1), subTabIndex }
}
